import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowUpDown, Download } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { userActivityApi } from '../../services/api';

const RANGES = [
  { label: 'Today',      value: 'today' },
  { label: 'This Week',  value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'This Year',  value: 'year' },
];

const FORMAT_COLORS = {
  pdf:    { bg: '#fee2e2', color: '#dc2626' },
  excel:  { bg: '#dcfce7', color: '#16a34a' },
  csv:    { bg: '#dbeafe', color: '#1d4ed8' },
  import: { bg: '#fef9c3', color: '#854d0e' },
};

const formatParamValue = (val) => {
  if (val === null || val === undefined) return '—';
  if (Array.isArray(val)) {
    return val.map(item =>
      item && typeof item === 'object' ? (item.display ?? item.value ?? String(item)) : String(item)
    ).join(', ');
  }
  if (typeof val === 'object') return val.display ?? val.value ?? JSON.stringify(val);
  return String(val);
};

const ParamsPopover = ({ params }) => {
  const [open, setOpen] = useState(false);
  const entries = params ? Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '') : [];
  if (entries.length === 0) return <span style={{ color: 'var(--color-gray-400)', fontSize: '11px' }}>—</span>;
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
      >
        View ({entries.length})
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 999 }} />
          <div style={{
            position: 'absolute', top: '120%', left: 0, zIndex: 1000,
            background: '#fff', border: '1px solid var(--color-gray-200)',
            borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            padding: '10px 14px', minWidth: 220, maxWidth: 340,
          }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--color-gray-600)', marginBottom: 8 }}>Parameters Used</div>
            {entries.map(([key, val]) => (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4, fontSize: '11px' }}>
                <span style={{ color: 'var(--color-gray-500)', flexShrink: 0 }}>{key}</span>
                <span style={{ color: 'var(--color-gray-800)', fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>
                  {formatParamValue(val)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ActivityHistoryPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [range, setRange]       = useState('today');
  const [search, setSearch]     = useState('');
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [sortCol, setSortCol]   = useState('createdAt');
  const [sortDir, setSortDir]   = useState('desc');

  const fetchHistory = useCallback((showSpinner = false) => {
    if (!currentUser?.id) return;
    if (showSpinner) setLoading(true);
    userActivityApi.getHistory(String(currentUser.id), range)
      .then(res => setRows(res.data || []))
      .catch(() => {})
      .finally(() => { if (showSpinner) setLoading(false); });
  }, [currentUser?.id, range]);

  // Initial load (with spinner) + poll every 10s (silent)
  useEffect(() => {
    fetchHistory(true);
    const interval = setInterval(() => fetchHistory(false), 10000);
    return () => clearInterval(interval);
  }, [fetchHistory]);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const data = q
      ? rows.filter(r =>
          r.name?.toLowerCase().includes(q) ||
          r.format?.toLowerCase().includes(q) ||
          r.machineName?.toLowerCase().includes(q) ||
          r.status?.toLowerCase().includes(q)
        )
      : rows;

    return [...data].sort((a, b) => {
      let av = a[sortCol] ?? '';
      let bv = b[sortCol] ?? '';
      if (sortCol === 'createdAt') { av = new Date(av); bv = new Date(bv); }
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  }, [rows, search, sortCol, sortDir]);

  const handleRegenerate = (row) => {
    try {
      const recipe = typeof row.exportRecipe === 'string' ? JSON.parse(row.exportRecipe) : row.exportRecipe;
      if (!recipe?.reportPath) return;
      const slug = recipe.dbSlug || localStorage.getItem('pmd-selected-db-slug') || 'gwa';
      navigate(`/${slug}${recipe.reportPath}`, {
        state: { autoExport: recipe.format, params: recipe.filterParams, dbSlug: slug }
      });
    } catch {
      // malformed recipe — silently ignore
    }
  };

  const SortHeader = ({ col, children }) => (
    <th onClick={() => handleSort(col)} style={{ cursor: 'pointer' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {children}
        <ArrowUpDown size={10} style={{ opacity: sortCol === col ? 1 : 0.3 }} />
      </span>
    </th>
  );

  return (
    <div className="activity-history-page">
      <div className="activity-history-title">Data Transfer History</div>

      <div className="activity-history-controls">
        {/* Range filter */}
        <div className="pref-pill-group">
          {RANGES.map(r => (
            <button
              key={r.value}
              className={`history-range-pill${range === r.value ? ' selected' : ''}`}
              onClick={() => setRange(r.value)}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="history-search-box">
          <Search size={13} style={{ color: 'var(--color-gray-400)' }} />
          <input
            placeholder="Search name, format, machine..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-gray-400)', fontSize: '13px' }}>
          Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-gray-400)', fontSize: '13px', fontStyle: 'italic' }}>
          No activity found for this period.
        </div>
      ) : (
        <table className="history-table">
          <thead>
            <tr>
              <SortHeader col="name">Name</SortHeader>
              <SortHeader col="format">Format</SortHeader>
              <SortHeader col="type">Type</SortHeader>
              <SortHeader col="status">Status</SortHeader>
              <th>File Name</th>
              <th>Parameters</th>
              <th>Database</th>
              <SortHeader col="userName">User</SortHeader>
              <SortHeader col="machineName">Machine</SortHeader>
              <SortHeader col="createdAt">Time</SortHeader>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(row => {
              const fmtColor = FORMAT_COLORS[row.format] || { bg: 'var(--color-gray-100)', color: 'var(--color-gray-600)' };
              const isCleared = !!row.clearedAt;
              const canRedownload = row.type === 'download' && !!row.exportRecipe;
              const ts = row.createdAt ? new Date(row.createdAt) : null;
              const timeStr = ts ? ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' ' + ts.toLocaleDateString([], { month: 'short', day: 'numeric' }) : '—';
              const recipe = (() => { try { return row.exportRecipe ? (typeof row.exportRecipe === 'string' ? JSON.parse(row.exportRecipe) : row.exportRecipe) : null; } catch { return null; } })();
              const fileName = recipe?.fileName || null;
              const filterParams = recipe?.filterParams || null;

              return (
                <tr key={row.id} className={isCleared ? 'cleared' : ''}>
                  <td style={{ maxWidth: 260 }}>
                    <span style={{ fontWeight: 600, fontSize: '12px' }}>{row.name}</span>
                    {isCleared && (
                      <span style={{ marginLeft: 6, fontSize: '9px', background: 'var(--color-gray-200)', color: 'var(--color-gray-500)', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>
                        CLEARED
                      </span>
                    )}
                  </td>
                  <td>
                    <span style={{ background: fmtColor.bg, color: fmtColor.color, fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>
                      {row.format?.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ textTransform: 'capitalize', fontSize: '11px' }}>{row.type}</td>
                  <td>
                    <span className={`history-status-pill history-status-${row.status === 'complete' ? 'complete' : row.status === 'failed' ? 'failed' : 'active'}`}>
                      {row.status}
                    </span>
                  </td>
                  <td style={{ fontSize: '10px', color: 'var(--color-gray-500)', maxWidth: 200, wordBreak: 'break-word' }}>
                    {fileName || '—'}
                  </td>
                  <td><ParamsPopover params={filterParams} /></td>
                  <td style={{ fontSize: '11px', color: 'var(--color-gray-500)' }}>{recipe?.dbName || recipe?.dbSlug || '—'}</td>
                  <td style={{ fontSize: '11px', color: 'var(--color-gray-500)' }}>{row.userName || '—'}</td>
                  <td style={{ fontSize: '11px', color: 'var(--color-gray-500)' }}>{row.machineName || '—'}</td>
                  <td style={{ fontSize: '11px', color: 'var(--color-gray-500)', whiteSpace: 'nowrap' }}>{timeStr}</td>
                  <td>
                    {canRedownload && (
                      <button
                        onClick={() => handleRegenerate(row)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          fontSize: '11px', fontWeight: 600,
                          padding: '3px 10px', borderRadius: 5,
                          border: '1px solid var(--color-primary)',
                          background: 'var(--color-primary-light)',
                          color: 'var(--color-primary)',
                          cursor: 'pointer'
                        }}
                        title="Navigate to report and regenerate with same parameters"
                      >
                        <Download size={11} /> Regenerate
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ActivityHistoryPage;
